import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { electronApis } from '@/features/electron-api';
import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';
import { AXON_MCP_SERVERS_QUERY_KEY } from '@/features/settings/sub-settings/settings-local-mcp.query';

import {
  buildLocalPathTransport,
  type LocalPathSetupFailure,
  type TrustedLocalPathSetup,
} from './local-path-setup';

import type {
  ConnectionTestResult,
  ServerEntry,
  ServerEntryInput,
} from '@littlebird/axon-mcp';

export type LocalPathSetupStatus = 'idle' | 'connecting' | 'failure';

interface UseLocalPathSetupOptions {
  catalogEntry: OnDeviceMcpCatalogEntry;
  setup: TrustedLocalPathSetup;
  onConnected?: () => void;
}

export function useLocalPathSetup({
  catalogEntry,
  setup,
  onConnected,
}: UseLocalPathSetupOptions) {
  const queryClient = useQueryClient();
  const attemptRef = useRef(0);
  const savingRef = useRef(false);
  const [paths, setPaths] = useState<readonly string[]>([]);
  const [status, setStatus] = useState<LocalPathSetupStatus>('idle');
  const [failure, setFailure] = useState<LocalPathSetupFailure | null>(null);
  const [canCancel, setCanCancel] = useState(true);

  const cancel = useCallback(() => {
    if (savingRef.current) return false;
    attemptRef.current += 1;
    setPaths([]);
    setStatus('idle');
    setFailure(null);
    return true;
  }, []);

  useEffect(
    () => () => {
      if (!savingRef.current) attemptRef.current += 1;
    },
    []
  );

  const pickPath = useCallback(async () => {
    const attempt = attemptRef.current;
    const selectedPath = await electronApis.axonMcp.pickDirectory();
    if (attemptRef.current !== attempt || !selectedPath) return;

    setPaths((currentPaths) => {
      if (currentPaths.includes(selectedPath)) return currentPaths;
      setFailure(null);
      return [...currentPaths, selectedPath];
    });
  }, []);

  const removePath = useCallback((path: string) => {
    setPaths((currentPaths) => {
      if (!currentPaths.includes(path)) return currentPaths;
      setFailure(null);
      return currentPaths.filter((currentPath) => currentPath !== path);
    });
  }, []);

  const connect = useCallback(async () => {
    if (paths.length === 0 || status === 'connecting') return;

    const attempt = ++attemptRef.current;
    const selectedPaths = [...paths];
    const transport = buildLocalPathTransport(catalogEntry, selectedPaths);
    const fail = (nextFailure: LocalPathSetupFailure) => {
      if (attemptRef.current !== attempt) return;
      setStatus('failure');
      setFailure(nextFailure);
    };

    setStatus('connecting');

    let testResult: ConnectionTestResult;
    try {
      const pathResult =
        await electronApis.axonMcp.checkLocalPaths(selectedPaths);
      if (attemptRef.current !== attempt) return;
      if (!pathResult.ok) {
        fail({ problem: pathResult.problem, path: pathResult.path });
        return;
      }

      testResult = await electronApis.axonMcp.testServer(
        transport,
        catalogEntry.name,
        setup.id
      );
      if (attemptRef.current !== attempt) return;
      if (!testResult.ok) {
        fail({ problem: testResult.problem ?? 'connection_failed' });
        return;
      }
    } catch {
      fail({ problem: 'connection_failed' });
      return;
    }

    let updatedServers: ServerEntry[];
    try {
      const currentServers = await electronApis.axonMcp.getServers();
      if (attemptRef.current !== attempt) return;
      if (
        currentServers.some(
          (server) => templateIdForServer(server) === setup.id
        )
      ) {
        fail({ problem: 'already_connected' });
        return;
      }

      const input: ServerEntryInput = {
        name: catalogEntry.name,
        description: catalogEntry.description,
        template: setup.id,
        transport,
        lastConnectionTest: {
          ok: true,
          ...(testResult.serverName && { serverName: testResult.serverName }),
          toolCount: testResult.toolCount ?? 0,
          testedAt: new Date().toISOString(),
        },
      };
      savingRef.current = true;
      setCanCancel(false);
      updatedServers = await electronApis.axonMcp.addServer(input);
      savingRef.current = false;
      setCanCancel(true);
      if (attemptRef.current !== attempt) return;
    } catch {
      savingRef.current = false;
      setCanCancel(true);
      fail({ problem: 'save_failed' });
      return;
    }

    setStatus('idle');
    setFailure(null);
    queryClient.setQueryData(AXON_MCP_SERVERS_QUERY_KEY, updatedServers);
    onConnected?.();
  }, [catalogEntry, onConnected, paths, queryClient, setup.id, status]);

  return {
    paths,
    status,
    failure,
    canConnect: paths.length > 0 && status !== 'connecting',
    canCancel,
    pickPath,
    removePath,
    connect,
    cancel,
  };
}

export type LocalPathSetupForm = ReturnType<typeof useLocalPathSetup>;
